import React from "react";
import { useWizard, BUSINESS_TYPES } from "../../lib/wizardStore";
import { CheckCircle2, AlertCircle } from "lucide-react";

function Section({ title, stepIdx, onEdit, children }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-heading text-base font-semibold text-slate-900">{title}</h3>
        <button
          onClick={() => onEdit(stepIdx)}
          data-testid={`edit-section-${stepIdx}`}
          className="text-xs font-medium text-blue-600 hover:underline"
        >
          Edit
        </button>
      </div>
      {children}
    </div>
  );
}

function Field({ label, value }) {
  return (
    <div className="flex justify-between gap-4 py-1.5 text-sm">
      <span className="text-slate-500">{label}</span>
      <span className={`text-right ${value ? "text-slate-800 font-medium" : "text-slate-400 italic"}`}>
        {value || "Missing"}
      </span>
    </div>
  );
}

export default function ReviewStep({ onEdit }) {
  const { state, completeness } = useWizard();
  const bt = BUSINESS_TYPES.find((b) => b.id === state.businessType);

  return (
    <div className="space-y-5">
      <div>
        <h2 className="font-heading text-2xl md:text-3xl font-semibold text-slate-900">
          Review your setup
        </h2>
        <p className="text-slate-500 mt-2">
          Looks good? Approve to continue. Need to tweak something? Just click Edit.
        </p>
      </div>

      {/* Completeness */}
      <div className="rounded-2xl border border-slate-200 bg-gradient-to-br from-blue-50/40 to-emerald-50/40 p-5">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium text-slate-700">Profile completeness</span>
          <span className="font-heading text-2xl font-semibold text-slate-900" data-testid="completeness-pct">
            {completeness.pct}%
          </span>
        </div>
        <div className="h-2 bg-white rounded-full overflow-hidden">
          <div
            className="h-full bg-gradient-to-r from-blue-500 to-emerald-500 transition-all duration-500"
            style={{ width: `${completeness.pct}%` }}
          />
        </div>
        {completeness.missing.length > 0 ? (
          <div className="mt-4">
            <div className="text-xs font-medium text-amber-700 uppercase tracking-wider flex items-center gap-1.5 mb-2">
              <AlertCircle className="w-3.5 h-3.5" /> Still missing
            </div>
            <div className="flex flex-wrap gap-1.5">
              {completeness.missing.map((m) => (
                <span key={m} className="text-xs bg-amber-50 text-amber-800 border border-amber-200 px-2 py-1 rounded-full">
                  {m}
                </span>
              ))}
            </div>
          </div>
        ) : (
          <div className="mt-3 text-sm text-emerald-700 flex items-center gap-1.5">
            <CheckCircle2 className="w-4 h-4" /> Looking complete!
          </div>
        )}
      </div>

      <Section title="Business info" stepIdx={1} onEdit={onEdit}>
        <Field label="Name" value={state.businessInfo.name} />
        <Field label="Phone" value={state.businessInfo.phone} />
        <Field label="Address" value={state.businessInfo.address} />
        <Field label="Website" value={state.businessInfo.website} />
        <Field label="Service area" value={state.businessInfo.serviceArea} />
      </Section>

      <Section title="Category" stepIdx={2} onEdit={onEdit}>
        <Field label="Business type" value={bt ? bt.label : ""} />
        <Field label="Google category" value={bt ? bt.category : ""} />
      </Section>

      <Section title="Services" stepIdx={3} onEdit={onEdit}>
        <p className="text-sm text-slate-700 whitespace-pre-wrap leading-relaxed">
          {state.services || <span className="text-slate-400 italic">Missing</span>}
        </p>
      </Section>

      <Section title="Hours" stepIdx={4} onEdit={onEdit}>
        <div className="space-y-1.5">
          {Object.entries(state.hours).map(([day, h]) => (
            <div key={day} className="flex justify-between text-sm">
              <span className="text-slate-500">{day}</span>
              <span className="text-slate-800 font-medium">
                {h.closed ? <span className="text-slate-400">Closed</span> : `${h.open} – ${h.close}`}
              </span>
            </div>
          ))}
        </div>
      </Section>

      <Section title="Photos" stepIdx={5} onEdit={onEdit}>
        <div className="grid grid-cols-2 gap-2">
          {Object.entries(state.photos).map(([k, v]) => (
            <div key={k} className="flex justify-between text-sm py-1">
              <span className="text-slate-500 capitalize">{k.replace("_", " ")}</span>
              <span className={`font-medium ${v === "ready" ? "text-emerald-600" : "text-slate-400"}`}>
                {v === "ready" ? "Ready" : "Pending"}
              </span>
            </div>
          ))}
        </div>
      </Section>
    </div>
  );
}
